import React from 'react';

/**
 * Wordmark — the "listicity.app" brand lockup.
 *
 * Composed of a compact three-line mark (a tiny list icon that doubles as the
 * logo) and the wordmark itself, styled as "lis" + accent "ti" + "city.app".
 * Used in both desktop + mobile headers and on the login page. Callers can
 * scale via fontSize, drop the ".app" suffix with showSuffix=false, or hide
 * the mark entirely with showMark=false.
 */
export default function Wordmark({
  fontSize = 15,
  showMark = true,
  showSuffix = true,
  // When true, bases sizes on the UI scale variable so the lockup grows with
  // the user's font-size setting. Set false on the login page where we pick
  // a fixed visual size.
  scaleWithUi = true,
  style,
}) {
  const scaled = (n) =>
    scaleWithUi ? `calc(${n}px * var(--ui-scale, 1))` : `${n}px`;
  // The mark icon should feel roughly the same visual weight as a capital
  // letter — a touch smaller than the text, centered on the baseline.
  const markSize = Math.round(fontSize * 0.95);

  return (
    <span
      aria-label="listicity.app"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: scaled(Math.round(fontSize * 0.42)),
        fontFamily: 'var(--mono)',
        fontSize: scaled(fontSize),
        fontWeight: 600,
        letterSpacing: '-0.5px',
        color: 'var(--text)',
        lineHeight: 1,
        ...style,
      }}
    >
      {showMark && <Mark size={markSize} scaleWithUi={scaleWithUi} />}
      <span style={{ display: 'inline-flex' }}>
        <span style={{ color: 'var(--text)' }}>lis</span>
        <span style={{ color: 'var(--accent)' }}>ti</span>
        <span style={{ color: 'var(--text)' }}>city</span>
        {showSuffix && (
          <span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
            .app
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * Mark — the three-line glyph that sits to the left of the wordmark. Three
 * short horizontal strokes stacked vertically, with the middle stroke tinted
 * accent so it echoes the "ti" highlight in the wordmark.
 */
function Mark({ size = 14, scaleWithUi = true }) {
  const dim = scaleWithUi ? `calc(${size}px * var(--ui-scale, 1))` : `${size}px`;
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {/* Top stroke */}
      <rect x="1" y="2.5" width="12" height="1.8" rx="0.9" fill="var(--text)" />
      {/* Middle stroke — accent, slightly shorter so the mark has rhythm */}
      <rect x="1" y="6.1" width="9"  height="1.8" rx="0.9" fill="var(--accent)" />
      {/* Bottom stroke */}
      <rect x="1" y="9.7" width="12" height="1.8" rx="0.9" fill="var(--text)" />
    </svg>
  );
}
